// @ts-nocheck — mixin methods are composed onto BackendService at runtime; instance API is typed via adminBackendServiceInterface.ts.
import type BaseBackendService from '../BaseBackendService';
import AdminUser from 'AdminUser';
import BackendAuthHandler from 'BackendAuthHandler';
import { GlobalSearchResult } from 'GlobalSearchResult';
import {
  PermissionDefinition,
  UserPermissions,
  VeraPromptProposal,
  VeraIntentPromptVersion,
  VeraGlobalChatPromptOverride,
} from '../../models/admin';
import { Article, ArticleContent } from '../../models/Article';
import Contact from '../../models/Contact';
import FundOpportunity from '../../models/FundOpportunity';
import {
  CreateInvestmentEntityRequest,
  InvestmentEntity,
  InvestorAllocationsResponse,
} from '../../models/InvestmentEntity';
export const adminBackendServiceSearchMixin = {
  normalizeAssetUrl(relativeUrl: string): string {
    return this.apiURL(relativeUrl).replace('//', '/');
  },

  async getAuthToken(): Promise<string> {
    return (await new BackendAuthHandler().withAuthToken())!;
  },

  downloadFAQ(): Promise<any> {
    return this.authApiGet('faq/download');
  },

  uploadFAQ(data: any): Promise<void> {
    return this.authApiPost('faq/upload', data, () => undefined);
  },

  generateArbitraryImageUploadUrl(
    path: string,
    fileExtension: string,
  ): Promise<{ uploadUrl: string; relativePath: string }> {
    return this.authFetchPromise(async (token) => {
      const genUrl = this.apiURL(
        `generateArbitraryImageUploadUrl?path=${encodeURIComponent(path)}&fileExtension=${encodeURIComponent(fileExtension)}`,
      );
      const response = await this.authenticatedFetch(genUrl, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        const text = await response.text();
        throw new Error(`${response.status} ${text}`);
      }
      const genData = await response.json();
      const uploadUrl: string = genData.uploadUrl || genData.url || genData.signedUrl;
      const relativePath: string = genData.relativePath || `${path}.${fileExtension}`;
      if (!uploadUrl) {
        throw new Error('No uploadUrl returned');
      }
      return { uploadUrl, relativePath };
    });
  },

  listImages(prefix: string): Promise<{ relativePath: string; contentType: string }[]> {
    return this.authApiGet(`listImages?prefix=${encodeURIComponent(prefix)}`, (data) =>
      (data.images || []).map((image: any) => ({
        relativePath: image.relativePath,
        contentType: image.contentType,
      })),
    );
  },

  globalSearch(
    term: string,
    includeRestrictedContacts: boolean = false,
    types?: string[],
    includeArticles: boolean = false,
  ): Promise<GlobalSearchResult[]> {
    return this.authFetchPromise(async (token) => {
      const params = new URLSearchParams({
        term: encodeURIComponent(term),
      });

      if (includeRestrictedContacts) {
        params.append('includeRestrictedContacts', 'true');
      }

      if (includeArticles) {
        params.append('includeArticles', 'true');
      }

      if (types && types.length > 0) {
        types.forEach((type) => params.append('type[]', type));
      }

      const response = await this.authenticatedFetch(
        this.apiURL(`globalSearch?${params.toString()}`),
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (!response.ok) {
        throw await response.json().catch(() => new Error(String(response.status)));
      }

      const json = await response.json();
      console.log(`Global search results: from /api/globalSearch?${params.toString()}`, json.data);
      return json.data as GlobalSearchResult[];
    });
  },

  searchContacts(
    term: string,
    includeRestrictedContacts: boolean = false,
  ): Promise<GlobalSearchResult[]> {
    return this.globalSearch(term, includeRestrictedContacts, ['Contact']);
  },

  searchAdminUsers(term: string): Promise<GlobalSearchResult[]> {
    return this.globalSearch(term, false, ['AdminUser']);
  },

  searchContactsAndAdminUsers(
    term: string,
    includeRestrictedContacts: boolean = false,
  ): Promise<GlobalSearchResult[]> {
    return this.globalSearch(term, includeRestrictedContacts, ['Contact', 'AdminUser']);
  },

  searchFundOpportunities(term: string): Promise<GlobalSearchResult[]> {
    return this.globalSearch(term, false, ['FundOpportunity']);
  },

  searchInvestmentEntities(term: string): Promise<GlobalSearchResult[]> {
    return this.globalSearch(term, false, ['InvestmentEntity']);
  },

  searchArticles(term: string): Promise<GlobalSearchResult[]> {
    return this.globalSearch(term, false, ['Article']);
  },

  getSelfPermissions(): Promise<UserPermissions> {
    return this.authApiGetAuthed(
      'adminUsers/me/permissions',
      'Network error while fetching self permissions',
      (json) => json.data.actionGrants as UserPermissions,
    );
  },

  getCurrentUser(): Promise<AdminUser> {
    return this.authApiGetAuthed(
      '/api/adminUsers/me',
      'Network error while fetching current user info',
      (json) => json.data as AdminUser,
      true,
    );
  },
} satisfies ThisType<BaseBackendService>;
